import { AuthContext, useApi } from '@repo-level/core-front';
import type { BonanzaPromoApi, BonanzaStatusItems } from 'api/cms/bonanzaPromo';
import { bonanzaPromo } from 'api/cms/bonanzaPromo';
import {
  BonanzaStatus,
  bonanzaPromoProgressPercent,
  checkIsBonanzaPromoAvailable,
  getCurrentBonanzaIterationDays,
  getUserBonanzaGroup,
  getUserReward,
} from 'helpers/bonanza';
import { amountWithCurrencySymbol } from 'helpers/currency';
import { BonanzaRestrictedGroups, someGroupMember } from 'helpers/user';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface BonanzaContextData {
  userBonanzaGroup: BonanzaStatus;
  bonanzaReward: string;
  bonanzaData: BonanzaPromoApi | null;
  bonanzaLevelItem: UpdatedBonanzaLevelItems | null;
  isBonanzaPromoAvailable: boolean;
}

interface UpdatedBonanzaLevelItems
  extends Omit<BonanzaStatusItems, 'promoLevel'> {
  progressPercent: number;
}

interface BonanzaContextActions {
  updateBoxData: () => void;
}

const initialValues = {
  userBonanzaGroup: BonanzaStatus.LEVEL0,
  bonanzaReward: '',
  bonanzaData: null,
  bonanzaLevelItem: null,
  isBonanzaPromoAvailable: false,
};

export const BonanzaContext = createContext<
  BonanzaContextData & BonanzaContextActions
>(null as any);

export const BonanzaContextProvider: FC = ({ children }) => {
  const { user } = useContext(AuthContext);
  const {
    i18n: { language },
  } = useTranslation();

  const [state, setState] = useState<BonanzaContextData>(initialValues);
  const [requestBonanzaPromo, { data: bonanzaCMSData, error: bonanzaError }] =
    useApi(bonanzaPromo);

  const updateBoxData = useCallback(() => {
    if (bonanzaCMSData) {
      const userBonanzaGroup = getUserBonanzaGroup(
        bonanzaCMSData.promoPrefix,
        user?.statuses
      );

      const userReward = getUserReward(user);

      const { isLastIteration } = getCurrentBonanzaIterationDays(
        bonanzaCMSData.start,
        bonanzaCMSData.end,
        language
      );

      const isPromoAvailable =
        !someGroupMember(BonanzaRestrictedGroups)(user) &&
        checkIsBonanzaPromoAvailable(bonanzaCMSData, user) &&
        !(isLastIteration && userBonanzaGroup === BonanzaStatus.PROMO_LOCKED);

      const updatedBonanzaStatusData = bonanzaCMSData.bonanzaStatus.reduce(
        (acc, { character, label, labelMob, promoLevel }) => {
          const promoLevelKey = promoLevel as keyof typeof BonanzaStatus;
          const bonanzaStatusKey = BonanzaStatus[promoLevelKey];

          acc[bonanzaStatusKey] = {
            progressPercent: bonanzaPromoProgressPercent[bonanzaStatusKey],
            character,
            label,
            labelMob,
          };

          return acc;
        },
        {} as Record<BonanzaStatus, UpdatedBonanzaLevelItems>
      );

      setState((prevState) => ({
        ...prevState,
        userBonanzaGroup,
        bonanzaReward: amountWithCurrencySymbol(
          userReward.currency,
          userReward.reward
        ),
        bonanzaData: bonanzaCMSData,
        bonanzaLevelItem: updatedBonanzaStatusData[userBonanzaGroup],
        isBonanzaPromoAvailable: isPromoAvailable,
      }));
    } else if (bonanzaError && bonanzaError.message !== 'canceled') {
      setState((prevState) => ({
        ...prevState,
        isBonanzaPromoAvailable: false,
      }));
    }
  }, [bonanzaCMSData, bonanzaError, user, language]);

  useEffect(() => {
    requestBonanzaPromo(language);
  }, [language, requestBonanzaPromo]);

  useEffect(() => {
    updateBoxData();
  }, [updateBoxData]);

  return (
    <BonanzaContext.Provider
      value={{
        ...state,
        updateBoxData,
      }}
    >
      {children}
    </BonanzaContext.Provider>
  );
};
